import { Box, Table as ChakraTable, Text } from '@chakra-ui/react';
import { FormControllerChildrenProps } from '@components/form';
import React, { JSX, useState } from 'react';
import { getElementDimensions } from '../utils';

export type TableColumnType<T> = keyof T & string;
export type TableColumnConfigOptionsType = {
  label: string;
  width?: string;
};
export type TableColumnConfigType<T> = Record<
  TableColumnType<T>,
  TableColumnConfigOptionsType
>;

export type TableColumnsConfigType<T> = Array<
  Partial<Record<TableColumnType<T>, TableColumnConfigOptionsType>>
>;

export type TableRenderCellProps<T extends Record<string, any>> = {
  colKey: keyof T;
  cellValue: any; // Column value
  rowValue: T; // Row Item of the table
  rowIdx: number; // Row Index of the table
  formControls?: FormControllerChildrenProps<any>;
};

export interface TableProps<T extends Record<string, any>> {
  columns: TableColumnsConfigType<T>;
  items: T[];
  height?: string; // To set the height of the table
  width?: string | number | any; // To set the width of the table
  renderCell?: (props: TableRenderCellProps<T>) => React.ReactNode; // To customize cell content
  hideColumns?: (keyof T)[]; // To hide columns
  formControls?: FormControllerChildrenProps<any>;

  [key: string]: any;
}

const TableMain = <T extends Record<string, any>>(props: TableProps<T>) => {
  const {
    columns: columnsProp,
    items,
    renderCell,
    width,
    height,
    hideColumns = [],
    formControls,
    ...rest
  } = props;

  const [columns, setColumns] = useState<TableColumnType<any>[]>([]);
  const [columnsConfig, setColumnsConfig] = useState<
    TableColumnConfigType<any>
  >({});

  React.useEffect(() => {
    const tempVisibleColumns: TableColumnType<any>[] = [];
    const tempColumnsConfig: TableColumnConfigType<any> = {};
    if (columnsProp.length > 0) {
      columnsProp.forEach((colObj) => {
        const [colKey, colConfig] = Object.entries(colObj)[0] as [
          TableColumnType<T>,
          TableColumnConfigOptionsType,
        ];
        tempVisibleColumns.push(colKey);
        const tempColConfig = {
          ...colConfig,
          label: colConfig.label ?? colKey,
          width: colConfig.width ?? '150px',
        };
        tempColumnsConfig[colKey] = tempColConfig;
      });
      setColumns(tempVisibleColumns);
      setColumnsConfig(tempColumnsConfig);
    }
  }, [columnsProp]);

  return (
    <Box
      width="100%"
      height="100%"
      bg="bg.subtle/50"
      rounded="md"
      p={2}
      ref={getElementDimensions}
    >
      <ChakraTable.ScrollArea
        borderWidth="1px"
        rounded="md"
        scrollbarWidth="none"
        justifySelf="center"
        alignSelf="center"
        minH={height}
        maxH={height}
        maxW={width}
        {...rest}
      >
        <ChakraTable.Root size="sm" stickyHeader interactive>
          <ChakraTable.Header>
            <ChakraTable.Row
              key="header-row"
              display="flex"
              bg="bg.subtle"
              width="fit-content"
            >
              {columns.map((colKey, colIdx) => {
                return (
                  <ChakraTable.ColumnHeader
                    key={String(colKey)}
                    color="tertiary.fg"
                    _hover={{
                      bg: 'bg.emphasized',
                    }}
                    borderRight="1px solid"
                    borderColor="border.subtle"
                    _last={{
                      borderRight: 'none',
                    }}
                    p={
                      columnsConfig[colKey].width === '0px' ||
                      columnsConfig[colKey].width === '0'
                        ? '0px'
                        : '3'
                    }
                  >
                    <Text
                      width={columnsConfig[colKey].width ?? '120px'}
                      textStyle="table.header"
                      textWrap="wrap"
                      textAlign="center"
                      gap="4px"
                      overflow="hidden"
                    >
                      {columnsConfig[colKey]?.label || colKey || ''}
                    </Text>
                  </ChakraTable.ColumnHeader>
                );
              })}
            </ChakraTable.Row>
          </ChakraTable.Header>
          <ChakraTable.Body>
            {items?.map((item, rowIdx) => (
              <ChakraTable.Row
                display="flex"
                key={`row-${String(rowIdx)}`}
                minWidth="fit-content"
                minHeight="fit-content"
              >
                {columns.map((colKey, colIdx) => {
                  return (
                    <ChakraTable.Cell
                      key={`cell-${String(rowIdx)}-${String(colKey)}`}
                      alignContent="center"
                      borderRight="1px solid"
                      borderColor="border.muted"
                      _last={{
                        borderRight: 'none',
                      }}
                      p={
                        columnsConfig[colKey].width === '0px' ||
                        columnsConfig[colKey].width === '0'
                          ? '0px'
                          : '3'
                      }
                    >
                      <Box
                        overflowY="scroll"
                        overflowX="hidden"
                        height={'100%'}
                        width={columnsConfig[colKey].width ?? 'auto'}
                        textWrap={'wrap'}
                        textAlign={'center'}
                      >
                        {renderCell
                          ? renderCell({
                              colKey,
                              cellValue: item[colKey],
                              rowValue: item,
                              rowIdx: rowIdx,
                              formControls: formControls,
                            })
                          : item[colKey] || ''}
                      </Box>
                    </ChakraTable.Cell>
                  );
                })}
              </ChakraTable.Row>
            ))}
          </ChakraTable.Body>
        </ChakraTable.Root>
      </ChakraTable.ScrollArea>
    </Box>
  );
};

export interface TableComponent {
  <T extends Record<string, any>>(props: TableProps<T>): JSX.Element;
}

const _Table = TableMain as unknown as TableComponent;

export const Table = _Table;

export namespace Table {
  export type RenderCellType<T extends Record<string, any>> =
    TableRenderCellProps<T>;
  export type ColumnType<T> = TableColumnType<T>;
  export type ColumnConfigType<T extends Record<string, any>> =
    TableColumnConfigType<T>;
  export type ColumnConfigOptionsType = TableColumnConfigOptionsType;
  export type ColumnsConfigType<T extends Record<string, any>> =
    TableColumnsConfigType<T>;
}
